import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { AlertTriangle, Lock, Mail } from 'lucide-react';
import SEO from '../components/SEO';

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

function AdminLoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate('/admin/dashboard');
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFF8F0] flex items-center justify-center p-6" data-testid="admin-login-page">
      <SEO title="Admin Login" noindex />
      <div className="w-full max-w-md">
        <div className="bg-white border border-[#E5E5E5] p-8">
          <div className="flex items-center justify-center gap-3 mb-8">
            <AlertTriangle className="text-[#E63946]" size={32} />
            <h1 className="text-3xl font-black tracking-tighter text-[#0A0A0A]" >
              ADMIN ACCESS
            </h1>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-xs tracking-[0.2em] uppercase font-bold text-[#8A8A8A] mb-2" >
                EMAIL
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8A8A]" size={18} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="admin-login-email-input"
                  required
                  className="w-full pl-10 pr-4 py-3 border border-[#E5E5E5] focus:outline-none focus:border-[#E63946] transition-colors"
                  
                  placeholder="admin@example.com"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-xs tracking-[0.2em] uppercase font-bold text-[#8A8A8A] mb-2" >
                PASSWORD
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8A8A]" size={18} />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="admin-login-password-input"
                  required
                  className="w-full pl-10 pr-4 py-3 border border-[#E5E5E5] focus:outline-none focus:border-[#E63946] transition-colors"
                  
                  placeholder="••••••••"
                />
              </div>
            </div>
            
            {error && (
              <div className="p-3 bg-[#E63946] bg-opacity-10 border border-[#E63946] text-[#E63946] text-sm" data-testid="admin-login-error" >
                {error}
              </div>
            )}
            
            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit-button"
              className="w-full py-3 bg-[#E63946] text-white font-bold text-sm tracking-[0.2em] uppercase hover:bg-[#d32f3e] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              
            >
              {loading ? 'LOGGING IN...' : 'LOGIN'}
            </button>
          </form>
        </div>
        
        <div className="mt-6 text-center">
          <a href="/" data-testid="back-to-home-link" className="text-sm text-[#8A8A8A] hover:text-[#E63946] transition-colors" >
            ← Back to Home
          </a>
        </div>
      </div>
    </div>
  );
}

export default AdminLoginPage;
